import { lazy, Suspense } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import AdminLayout from '../layouts/AdminLayout';
import HomePage from '../pages/HomePage';
import ProtectedRoute from '../components/ProtectedRoute';
import { ROUTES } from '../config/constants';

// Lazy-loaded pages
const ProfilePage = lazy(() => import('../pages/ProfilePage'));
const CartPage = lazy(() => import('../pages/CartPage'));
const WishlistPage = lazy(() => import('../pages/WishlistPage'));
const ProductDetailPage = lazy(() => import('../pages/ProductDetailPage'));
const CheckoutPage = lazy(() => import('../pages/CheckoutPage'));
const PaymentSuccess = lazy(() => import('../pages/PaymentSuccess'));
const PaymentError = lazy(() => import('../pages/PaymentError'));
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));

// Lazy-loaded admin pages
const AdminDashboard = lazy(() => import('../pages/admin/AdminDashboard'));
const ProductsManagement = lazy(() => import('../pages/admin/ProductsManagement'));
const CollectionsManagement = lazy(() => import('../pages/admin/CollectionsManagement'));
const CustomersManagement = lazy(() => import('../pages/admin/CustomersManagement'));
const OrdersManagement = lazy(() => import('../pages/admin/OrdersManagement'));
const ShippingManagement = lazy(() => import('../pages/admin/ShippingManagement'));
const SettingsManagement = lazy(() => import('../pages/admin/SettingsManagement'));

const SuspenseWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div></div>}>
    {children}
  </Suspense>
);

export const router = createBrowserRouter([
  {
    path: ROUTES.home,
    element: <MainLayout />,
    children: [
      {
        index: true,
        element: <HomePage />,
      },
      {
        path: ROUTES.profile,
        element: <SuspenseWrapper><ProfilePage /></SuspenseWrapper>,
      },
      {
        path: ROUTES.cart,
        element: <SuspenseWrapper><CartPage /></SuspenseWrapper>,
      },
      {
        path: ROUTES.wishlist,
        element: <SuspenseWrapper><WishlistPage /></SuspenseWrapper>,
      },
      {
        path: ROUTES.products,
        element: <SuspenseWrapper><ProductDetailPage /></SuspenseWrapper>,
      },
      {
        path: ROUTES.checkout,
        element: (
          <ProtectedRoute>
            <SuspenseWrapper><CheckoutPage /></SuspenseWrapper>
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.paymentSuccess,
        element: <SuspenseWrapper><PaymentSuccess /></SuspenseWrapper>,
      },
      {
        path: ROUTES.paymentError,
        element: <SuspenseWrapper><PaymentError /></SuspenseWrapper>,
      },
      {
        path: '*',
        element: <SuspenseWrapper><NotFoundPage /></SuspenseWrapper>,
      },
    ],
  },
  {
    path: ROUTES.admin,
    element: (
      <ProtectedRoute requireAdmin={true}>
        <AdminLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <SuspenseWrapper><AdminDashboard /></SuspenseWrapper>,
      },
      {
        path: 'products',
        element: <SuspenseWrapper><ProductsManagement /></SuspenseWrapper>,
      },
      {
        path: 'collections',
        element: <SuspenseWrapper><CollectionsManagement /></SuspenseWrapper>,
      },
      {
        path: 'customers',
        element: <SuspenseWrapper><CustomersManagement /></SuspenseWrapper>,
      },
      {
        path: 'orders',
        element: <SuspenseWrapper><OrdersManagement /></SuspenseWrapper>,
      },
      {
        path: 'shipping',
        element: <SuspenseWrapper><ShippingManagement /></SuspenseWrapper>,
      },
      {
        path: 'settings',
        element: <SuspenseWrapper><SettingsManagement /></SuspenseWrapper>,
      },
    ],
  },
]);
